import { useEffect, useRef } from "react";
import { gql } from "@apollo/client";
import { useApolloClient } from "@apollo/client/react";
import useProjectStore from "./projectStore";
import useCoreStore, { isFresh, CACHE_TTL_MS, SLICE_DEFS, _retries } from "./coreStore";
import { setItemDB, compressToIDB } from "../components/commons";
import { isMockExternalCmdbRecordsEnabled, generateMockExternalCmdbRecords } from "./mockExternalCmdbRecords";
import {
  _Q_hostsDiscovery,
  _Q_systemsDiscovery,
  _Q_getAllApplicationsPaged,
  _Q_getAllApplicationComponents,
  _Q_getHostsWithVulnerabilities,
  _Q_getHostsInstalledPackagesAgg,
  _Q_getEnrichmentResults,
  _Q_applicationsGraph,
  _Q_hostsGraph,
  _Q_getArtifactRepositories,
  _Q_getExternalCmdbRecords,
} from "../query/queries";

/**
 * CoreStoreProvider
 *
 * Mounted once at app level (inside ApolloProvider + AuthProvider).
 * Registers imperative fetch functions in coreStore — actual fetches are
 * triggered on demand by useEnsure* hooks.
 *
 * Adding a new endpoint:
 *  1. Add a GQL_* constant and a fetch function below
 *  2. Wire it to fetchRefs.<name> (add a useRef entry if it's a new fetch group)
 *  3. Add fetchRef: '<name>' to the slice's entry in SLICE_DEFS (coreStore.js)
 *  4. Export useEnsureX from useCoreData.js
 */


const GQL_HOSTS        = gql`${_Q_hostsDiscovery}`;
const GQL_SYSTEMS      = gql`${_Q_systemsDiscovery}`;
const GQL_APPS         = gql`query getCurrentApplicationsPaged($page: Int, $pageSize: Int) { ${_Q_getAllApplicationsPaged} }`;
const GQL_COMPONENTS   = gql`query getAllApplicationComponents { ${_Q_getAllApplicationComponents} }`;
const GQL_VULN         = gql`{ ${_Q_getHostsWithVulnerabilities} }`;
const GQL_PACKAGES     = gql`{ ${_Q_getHostsInstalledPackagesAgg} }`;
const GQL_ENRICHMENT   = gql`${_Q_getEnrichmentResults}`;
const GQL_APP_GRAPH    = gql`${_Q_applicationsGraph}`;
const GQL_HOSTS_GRAPH  = gql`${_Q_hostsGraph}`;
const GQL_ARTIFACT_REPOS = gql`${_Q_getArtifactRepositories}`;
const GQL_CMDB_RECORDS   = gql`${_Q_getExternalCmdbRecords}`;

/**
 * Cheap fingerprint: length + sorted ids + 3-item sample + max timestamp.
 * Detects adds, removes, and field changes without full serialisation.
 */
function fingerprint(arr, idKey = 'id', extraFields = []) {
  if (!arr?.length) return '[]';
  const ids = arr.map(item => item[idKey] ?? item.machineId ?? item.applicationId ?? '').sort();
  let maxTs = 0;
  for (let i = 0; i < arr.length; i++) {
    const t = arr[i].timestamp || arr[i].scanTime || arr[i].createdAt || 0;
    if (t > maxTs) maxTs = t;
  }
  const extra = extraFields.length
    ? ':' + arr.map(item => extraFields.map(f => item[f] ?? '').join('|')).join(',')
    : '';
  return `${arr.length}:${ids.join(',')}:${maxTs}${extra}`;
}

function dataUnchanged(prev, next, idKey = 'id', extraFields = []) {
  return fingerprint(prev, idKey, extraFields) === fingerprint(next, idKey, extraFields);
}

/** Generic paginated GraphQL fetch (network-only, no Apollo cache). */
export async function fetchAllPages(client, query, { variables = {}, paginated = false, pageSize = 1000 } = {}) {
  if (!paginated) {
    const result = await client.query({ query, fetchPolicy: "network-only", errorPolicy: "all", variables, context: { noRetry: true } });
    return result.data;
  }

  let allData = null;
  let currentPage = 1;
  let totalPages = 1;

  do {
    const result = await client.query({
      query, fetchPolicy: "network-only", errorPolicy: "all",
      variables: { ...variables, page: currentPage, pageSize },
      context: { noRetry: true },
    });

    if (!allData) {
      allData = result.data;
    } else {
      for (const topKey of Object.keys(result.data ?? {})) {
        const val = result.data[topKey];
        if (!val) continue;
        for (const innerKey of Object.keys(val)) {
          if (Array.isArray(val[innerKey]) && allData[topKey]?.[innerKey]) {
            allData[topKey][innerKey] = allData[topKey][innerKey].concat(val[innerKey]);
          }
        }
      }
    }

    let pageInfo = null;
    for (const topKey of Object.keys(result.data ?? {})) {
      if (result.data[topKey]?.pageInfo) { pageInfo = result.data[topKey].pageInfo; break; }
    }
    if (!pageInfo) break;
    totalPages = pageInfo.totalPages ?? 1;
    currentPage = (pageInfo.currentPage ?? currentPage) + 1;

    const anyArr = Object.values(result.data ?? {}).flatMap(v => Object.values(v ?? {})).find(Array.isArray);
    if (!anyArr?.length || currentPage > totalPages) break;
  } while (currentPage <= totalPages);

  return allData;
}

export default function CoreStoreProvider({ children, assessmentClient }) {
  const projectId = useProjectStore(s => s.currentProjectId);
  const client = useApolloClient();

  // One ref per fetch group (not per slice — some slices share a fetch).
  // Add a new entry here only when introducing a genuinely new fetch operation.
  const fetchRefs = {
    hosts:             useRef(null),
    applications:      useRef(null),
    secondary:         useRef(null), // vulnerabilities + installedPackages fetched together
    assessments:       useRef(null),
    enrichmentResults: useRef(null),
    artifactRepositories: useRef(null),
    externalCmdbRecords:  useRef(null),
    appGraphData:      useRef(null),
    hostsGraph:        useRef(null),
  };

  // ── Fetch functions — reassigned on every render so they always close over
  //    the latest client / projectId without needing re-registration in the store. ──

  fetchRefs.hosts.current = async () => {
    if (!projectId) return;
    useCoreStore.getState().setLoading("hostsLoading", true);
    try {
      const [hostsData, systemsData] = await Promise.all([
        fetchAllPages(client, GQL_HOSTS),
        fetchAllPages(client, GQL_SYSTEMS),
      ]);
      const hosts   = hostsData?.hosts?.hosts     ?? [];
      const systems = systemsData?.systems?.systems ?? [];
      const prev = useCoreStore.getState();
      if (dataUnchanged(prev.hosts, hosts, 'machineId') && dataUnchanged(prev.systems, systems) && hosts.length) {
        useCoreStore.getState().setHosts(prev.hosts, prev.systems, Date.now());
      } else {
        const ts = Date.now();
        useCoreStore.getState().setHosts(hosts, systems, ts);
        setItemDB(`hosts--${projectId}`,   { _data: hosts,   _cachedAt: ts }).catch(() => {});
        setItemDB(`systems--${projectId}`, { _data: systems, _cachedAt: ts }).catch(() => {});
      }
      _retries.hosts = 0;
    } catch (err) {
      console.error("[CoreStoreProvider] hosts fetch error", err);
    } finally {
      useCoreStore.getState().setLoading("hostsLoading", false);
    }
  };

  fetchRefs.secondary.current = async () => {
    if (!projectId) return;
    useCoreStore.getState().setLoading("vulnerabilitiesLoading", true);
    useCoreStore.getState().setLoading("installedPackagesLoading", true);
    try {
      const [vulnData, pkgData] = await Promise.all([
        fetchAllPages(client, GQL_VULN),
        fetchAllPages(client, GQL_PACKAGES),
      ]);
      const vulnerabilities    = vulnData?.getHostsWithVulnerabilities  ?? [];
      const installedPackagesAgg = pkgData?.getHostsInstalledPackagesAgg ?? [];
      
      const prev = useCoreStore.getState();
      const ts = Date.now();
      if (!dataUnchanged(prev.vulnerabilities, vulnerabilities, 'machineId') || !vulnerabilities.length) {
        useCoreStore.getState().setVulnerabilities(vulnerabilities, ts);
        compressToIDB({ _data: vulnerabilities, _cachedAt: ts })
          .then(buf => setItemDB(`hostsWithVulnerabilities--${projectId}`, buf))
          .catch(() => {});
      } else {
        useCoreStore.getState().setVulnerabilities(prev.vulnerabilities, ts);
      }
      if (!dataUnchanged(prev.installedPackagesAgg, installedPackagesAgg, 'machineId') || !installedPackagesAgg.length) {
        useCoreStore.getState().setInstalledPackagesAgg(installedPackagesAgg, ts);
        setItemDB(`installedPackagesAgg--${projectId}`, { _data: installedPackagesAgg, _cachedAt: ts }).catch(() => {});
      } else {
        useCoreStore.getState().setInstalledPackagesAgg(prev.installedPackagesAgg, ts);
      }
      _retries.vulnerabilities = 0;
      _retries.installedPackages = 0;
    } catch (err) {
      console.error("[CoreStoreProvider] secondary fetch error", err);
    } finally {
      useCoreStore.getState().setLoading("vulnerabilitiesLoading", false);
      useCoreStore.getState().setLoading("installedPackagesLoading", false);
    }
  };

  fetchRefs.applications.current = async () => {
    if (!projectId) return;
    useCoreStore.getState().setLoading("applicationsLoading", true);
    try {
      const appsData = await fetchAllPages(client, GQL_APPS, { paginated: true, pageSize: 1000 });
      let allApps = appsData?.getCurrentApplications?.apps ?? [];
      try {
        const compData = await fetchAllPages(client, GQL_COMPONENTS);
        const components = (compData?.getApplicationComponents ?? []).map(a => ({
          ...a, applicationTemplateId: "AppComponent", tags: [],
        }));
        allApps = allApps.concat(components);
      } catch (_) { /* components failure is non-fatal */ }
      const prev = useCoreStore.getState();
      // name/description are here because they are editable in place (the rename
      // dialog in AppDetailsPanel) without anything else about the app moving:
      // same id, same count, and no rescan so maxTs is unchanged too. Without
      // them the fingerprint matched after a rename, this branch kept the
      // previous array, and the table went on showing the old name even though
      // the fetch had returned the new one.
      if (dataUnchanged(prev.applications, allApps, 'id', ['dockerImageUrl', 'properties', 'name', 'description']) && allApps.length) {
        useCoreStore.getState().setApplications(prev.applications, Date.now());
      } else {
        const ts = Date.now();
        useCoreStore.getState().setApplications(allApps, ts);
        setItemDB(`applications--${projectId}`, { _data: allApps, _cachedAt: ts }).catch(() => {});
      }
      _retries.applications = 0;
    } catch (err) {
      console.error("[CoreStoreProvider] applications fetch error", err);
    } finally {
      useCoreStore.getState().setLoading("applicationsLoading", false);
    }
  };

  fetchRefs.assessments.current = async () => {
    if (!projectId || !assessmentClient) return;
    useCoreStore.getState().setLoading("assessmentsSummaryLoading", true);
    try {
      const result = await assessmentClient.query({
        query: gql`query assessmentsSummary {
          assessmentsSummary(projectId: "${projectId}") {
            applicationId filesCount loc findings hasReport
            infoFindings lowFindings mediumFindings highFindings
            score sbom vulnerabilities recommendation tags timestamp
          }
        }`,
        fetchPolicy: "network-only", errorPolicy: "all", context: { noRetry: true },
      });
      const assessmentsSummary = result.data?.assessmentsSummary ?? [];
      const ts = Date.now();
      useCoreStore.getState().setAssessmentsSummary(assessmentsSummary, ts);
      setItemDB(`assessmentsSummary--${projectId}`, { _data: assessmentsSummary, _cachedAt: ts }).catch(() => {});
      _retries.assessmentsSummary = 0;
    } catch (err) {
      console.error("[CoreStoreProvider] assessmentsSummary fetch error", err);
    } finally {
      useCoreStore.getState().setLoading("assessmentsSummaryLoading", false);
    }
  };

  fetchRefs.enrichmentResults.current = async () => {
    if (!projectId) return;
    useCoreStore.getState().setLoading("enrichmentResultsLoading", true);
    try {
      const result = await fetchAllPages(client, GQL_ENRICHMENT, { paginated: true, pageSize: 1000 });
      const enrichmentResults = result?.getEnrichmentResults?.results ?? [];
      const prev = useCoreStore.getState();
      if (dataUnchanged(prev.enrichmentResults, enrichmentResults) && enrichmentResults.length) {
        useCoreStore.getState().setEnrichmentResults(prev.enrichmentResults, Date.now());
      } else {
        const ts = Date.now();
        useCoreStore.getState().setEnrichmentResults(enrichmentResults, ts);
        setItemDB(`enrichmentResults--${projectId}`, { _data: enrichmentResults, _cachedAt: ts }).catch(() => {});
      }
      _retries.enrichmentResults = 0;
    } catch (err) {
      console.error("[CoreStoreProvider] enrichmentResults fetch error", err);
    } finally {
      useCoreStore.getState().setLoading("enrichmentResultsLoading", false);
    }
  };

  // Flat, non-versioned, tenant-scoped tables — no history/version variant,
  // fetched in full (like enrichmentResults) rather than paged in the UI.
  fetchRefs.artifactRepositories.current = async () => {
    if (!projectId) return;
    useCoreStore.getState().setLoading("artifactRepositoriesLoading", true);
    try {
      const result = await fetchAllPages(client, GQL_ARTIFACT_REPOS, { paginated: true, pageSize: 1000 });
      const artifactRepositories = result?.getArtifactRepositories?.results ?? [];
      const prev = useCoreStore.getState();
      if (dataUnchanged(prev.artifactRepositories, artifactRepositories) && artifactRepositories.length) {
        useCoreStore.getState().setArtifactRepositories(prev.artifactRepositories, Date.now());
      } else {
        const ts = Date.now();
        useCoreStore.getState().setArtifactRepositories(artifactRepositories, ts);
        setItemDB(`artifactRepositories--${projectId}`, { _data: artifactRepositories, _cachedAt: ts }).catch(() => {});
      }
      _retries.artifactRepositories = 0;
    } catch (err) {
      console.error("[CoreStoreProvider] artifactRepositories fetch error", err);
    } finally {
      useCoreStore.getState().setLoading("artifactRepositoriesLoading", false);
    }
  };

  fetchRefs.externalCmdbRecords.current = async () => {
    if (!projectId) return;
    useCoreStore.getState().setLoading("externalCmdbRecordsLoading", true);
    try {
      let externalCmdbRecords;
      if (isMockExternalCmdbRecordsEnabled()) {
        externalCmdbRecords = generateMockExternalCmdbRecords();
      } else {
        const result = await fetchAllPages(client, GQL_CMDB_RECORDS, { paginated: true, pageSize: 1000 });
        externalCmdbRecords = result?.getExternalCmdbRecords?.results ?? [];
      }
      const prev = useCoreStore.getState();
      if (dataUnchanged(prev.externalCmdbRecords, externalCmdbRecords) && externalCmdbRecords.length) {
        useCoreStore.getState().setExternalCmdbRecords(prev.externalCmdbRecords, Date.now());
      } else {
        const ts = Date.now();
        useCoreStore.getState().setExternalCmdbRecords(externalCmdbRecords, ts);
        setItemDB(`externalCmdbRecords--${projectId}`, { _data: externalCmdbRecords, _cachedAt: ts }).catch(() => {});
      }
      _retries.externalCmdbRecords = 0;
    } catch (err) {
      console.error("[CoreStoreProvider] externalCmdbRecords fetch error", err);
    } finally {
      useCoreStore.getState().setLoading("externalCmdbRecordsLoading", false);
    }
  };

  fetchRefs.appGraphData.current = async () => {
    if (!projectId) return;
    useCoreStore.getState().setLoading("appGraphDataLoading", true);
    try {
      const result = await fetchAllPages(client, GQL_APP_GRAPH);
      const appGraphData = result ?? null;
      const prev = useCoreStore.getState();
      const ts = Date.now();
      if (appGraphData && JSON.stringify(prev.appGraphData) === JSON.stringify(appGraphData)) {
        useCoreStore.getState().setAppGraphData(prev.appGraphData, ts);
      } else {
        useCoreStore.getState().setAppGraphData(appGraphData, ts);
        setItemDB(`appGraphData--${projectId}`, { _data: appGraphData, _cachedAt: ts }).catch(() => {});
      }
      _retries.appGraphData = 0;
    } catch (err) {
      console.error("[CoreStoreProvider] appGraphData fetch error", err);
    } finally {
      useCoreStore.getState().setLoading("appGraphDataLoading", false);
    }
  };

  /* The host graph, alongside the application one.
     -------------------------------------------------------------------------
     It was fetched ad hoc by whichever page wanted it, which meant the one
     page that wants both waited on the network for one of them every time it
     opened. It is a slice now, cached in IndexedDB beside its sibling, so the
     multiverse draws from the cache and refreshes behind the drawing.

     What is stored is the whole GraphQL result - { hostsGraph: {...} } - the
     way appGraphData stores { applicationsGraph: {...} }, because the IDB
     record and the store field have to agree and the sibling already set the
     shape. */
  fetchRefs.hostsGraph.current = async () => {
    if (!projectId) return;
    useCoreStore.getState().setLoading("hostsGraphLoading", true);
    try {
      const result = await fetchAllPages(client, GQL_HOSTS_GRAPH);
      const hostsGraph = result ?? null;
      const prev = useCoreStore.getState();
      const ts = Date.now();
      if (hostsGraph && JSON.stringify(prev.hostsGraph) === JSON.stringify(hostsGraph)) {
        useCoreStore.getState().setHostsGraph(prev.hostsGraph, ts);
      } else {
        useCoreStore.getState().setHostsGraph(hostsGraph, ts);
        setItemDB(`hostsGraph--${projectId}`, { _data: hostsGraph, _cachedAt: ts }).catch(() => {});
      }
      _retries.hostsGraph = 0;
    } catch (err) {
      console.error("[CoreStoreProvider] hostsGraph fetch error", err);
    } finally {
      useCoreStore.getState().setLoading("hostsGraphLoading", false);
    }
  };

  useEffect(() => {
    if (!projectId) return;

    useCoreStore.getState().resetAll();

    // Register stable wrapper lambdas — they always call through the ref so they
    // pick up the latest closure (client + projectId) without re-registration.
    const refetchMap = { refetchSystems: () => fetchRefs.hosts.current() };
    for (const def of SLICE_DEFS) {
      refetchMap[`refetch${def.key.charAt(0).toUpperCase() + def.key.slice(1)}`] =
        () => fetchRefs[def.fetchRef].current();
    }
    useCoreStore.getState().setRefetch(refetchMap);

    const handleInvalidated = () =>
      useCoreStore.getState().invalidate(SLICE_DEFS.map(d => d.key));
    window.addEventListener('appfactor:data-invalidated', handleInvalidated);

    const ttlInterval = setInterval(() => {
      const s = useCoreStore.getState();
      const stale = SLICE_DEFS
        .filter(d => !isFresh(s[`${d.key}CachedAt`]) && !s[`${d.key}Loading`] && s[`${d.key}CachedAt`] !== null)
        .map(d => d.key);
      if (stale.length) useCoreStore.getState().invalidate(stale);
    }, Math.max(Math.floor(CACHE_TTL_MS / 5), 10_000));

    return () => {
      window.removeEventListener('appfactor:data-invalidated', handleInvalidated);
      clearInterval(ttlInterval);
    };
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  return children;
}
